import i18next from 'i18next'
import * as React from 'react'
import {
  AsyncState,
  StandardLocalWidget,
  any,
  async,
  fromJSX,
  nothing,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../../subscription/subscriptionState'
import { ComponentDidUpdate } from '../../../../utils/lifecycleMethods'
import { ModalLayout } from '../../../../utils/modal'
import { componentDetailPath } from '../../../../utils/pages'
import { Redirect } from '../../../../utils/redirect'
import { PageHeaderLayout } from '../../pageHeader/pageHeaderLayout'
import { paywallWidget } from '../../paywall/paywallWidget'
import { RootLayout } from '../../rootLayout'
import {
  getIngredientsSearchResults,
  getMainIngredientsSearchResults,
  loadDataItems,
} from './componentEditApi'
import { ComponentEditLayout } from './componentEditLayout'
import {
  ComponentEditState,
  ComponentEditorState,
  componentEditStateUpdaters,
} from './componentEditState'
import { isDraft } from './componentEditorUtils'

export const componentEditWidget =
  (
    isLoggedIn: boolean,
    userSubscriptionState: AsyncState<UserSubscriptions>,
    componentId: number
  ): StandardLocalWidget<ComponentEditState> =>
  (componentEditState) =>
    any([
      async(componentEditState),
      componentEditState.kind === 'loaded'
        ? paywallWidget(isLoggedIn, userSubscriptionState)(
            componentEditorWidget(componentId, true)(componentEditState.value),
            componentEditorWidget(componentId, false)(componentEditState.value)
          ).map(componentEditStateUpdaters.componentEditorStateUpdater)
        : componentEditState.kind === 'loading'
        ? fromJSX(() => <RootLayout.Loader hasWrapper={true} showText={true} />)
        : fromJSX(() => <RootLayout.Error />),
    ])

const componentEditorWidget =
  (
    componentId: number,
    paywalled: boolean
  ): StandardLocalWidget<ComponentEditorState> =>
  (componentEditorState) => {
    const {
      newComponent,
      originalComponentName,
      editingState,
      deletingState,
      dataState,
      componentFields,
      warning,
      scrollTo,
    } = componentEditorState
    const {
      name,
      introduction,
      preparation,
      servingSuggestions,
      mainIngredients,
      ingredients,
      techniques,
      appearances,
      textures,
      worldFlavours,
    } = componentFields
    return any([
      async(editingState).map(componentEditStateUpdaters.editingStateUpdater),
      editingState.kind === 'loaded'
        ? fromJSX(() => (
            <Redirect
              to={componentDetailPath(
                typeof editingState.value === 'number'
                  ? editingState.value
                  : componentId,
                name
              )}
            />
          ))
        : nothing(),
      async(deletingState).map(componentEditStateUpdaters.deletingStateUpdater),
      deletingState.kind === 'loaded'
        ? fromJSX(() => (
            <Redirect
              to={
                i18next.language === 'en'
                  ? '/en/components/'
                  : '/nl/componenten/'
              }
            />
          ))
        : nothing(),
      async(dataState).map(componentEditStateUpdaters.dataStateUpdater),
      fromJSX((setState) => (
        <ComponentDidUpdate
          value={scrollTo}
          onUpdate={() => {
            if (scrollTo !== undefined) {
              scrollTo.scrollIntoView()
              setState((state) => ({ ...state, scrollTo: undefined }))
            }
          }}
        />
      )),
      dataState.kind === 'loaded'
        ? fromJSX((setState) => (
            <>
              <PageHeaderLayout
                kind="component-edit"
                title={
                  newComponent
                    ? i18next.t('componentEdit:makeVariantTitle')
                    : i18next.t('componentEdit:editVariantTitle')
                }
                intro={
                  newComponent
                    ? i18next.t('componentEdit:makeVariantIntro')
                    : i18next.t('componentEdit:editVariantIntro')
                }
              />
              <ComponentEditLayout.Wrapper>
                <ComponentEditLayout.ComponentFields
                  paywalled={paywalled}
                  name={{
                    value: name,
                    onChange: (newValue) =>
                      setState(
                        componentEditStateUpdaters.componentFieldValueUpdater(
                          'name'
                        )(() => newValue)
                      ),
                  }}
                  introduction={{
                    value: introduction,
                    onChange: (newValue) =>
                      setState(
                        componentEditStateUpdaters.componentFieldValueUpdater(
                          'introduction'
                        )(() => newValue)
                      ),
                  }}
                  preparation={{
                    value: preparation,
                    onChange: (newValue) =>
                      setState(
                        componentEditStateUpdaters.componentFieldValueUpdater(
                          'preparation'
                        )(() => newValue)
                      ),
                  }}
                  servingSuggestions={{
                    value: servingSuggestions,
                    onChange: (newValue) =>
                      setState(
                        componentEditStateUpdaters.componentFieldValueUpdater(
                          'servingSuggestions'
                        )(() => newValue)
                      ),
                  }}
                  ingredients={ingredients.map((ingredient, index) => ({
                    id: ingredient.id,
                    amount: {
                      value: ingredient.amount,
                      onChange: (newValue) =>
                        setState(
                          componentEditStateUpdaters.ingredientsAmountUpdater(
                            index
                          )(newValue)
                        ),
                    },
                    prepreparations: {
                      selected: ingredient.prepreparations,
                      options: dataState.value.prepreparations.map((p) => ({
                        ...p,
                        onChange: () =>
                          setState(
                            componentEditStateUpdaters.ingredientsPrepreparationsUpdater(
                              index
                            )(p)
                          ),
                      })),
                    },
                    unitOfMeasurement: {
                      selected: ingredient.unitOfMeasurement,
                      options: dataState.value.unitsOfMeasurement,
                      onChange: (value: string) =>
                        setState(
                          componentEditStateUpdaters.ingredientsUnitOfMeasurementUpdater(
                            index
                          )(value)
                        ),
                    },
                    ingredient: {
                      selected: ingredient.ingredient,
                      onSearchTerm: (term, callback) =>
                        loadDataItems(() =>
                          getIngredientsSearchResults(term)
                        ).then((result) => {
                          if (result.kind === 'result') {
                            callback(
                              result.value?.map((s) => ({
                                value: s.id,
                                label: s.translation,
                              })) ?? []
                            )
                          }
                        }),
                      onChangeValue: (newValue, _) =>
                        setState(
                          componentEditStateUpdaters.selectedIngredientSetter(
                            index
                          )(
                            newValue != undefined
                              ? {
                                  id: newValue.value,
                                  translation: newValue.label,
                                }
                              : undefined
                          )
                        ),
                    },
                    onRemove: () =>
                      setState(
                        componentEditStateUpdaters.removeIngredientUpdater(
                          index
                        )
                      ),
                    onMoveUp: (element?: HTMLSpanElement) =>
                      setState(
                        componentEditStateUpdaters.moveIngredientUpdater(
                          index,
                          'up',
                          element
                        )
                      ),
                    onMoveDown: (element?: HTMLSpanElement) =>
                      setState(
                        componentEditStateUpdaters.moveIngredientUpdater(
                          index,
                          'down',
                          element
                        )
                      ),
                  }))}
                  onAddIngredient={() =>
                    setState(componentEditStateUpdaters.addIngredientUpdater)
                  }
                  mainIngredients={{
                    selected: mainIngredients,
                    onSearchTerm: (term, callback) =>
                      loadDataItems(() =>
                        getMainIngredientsSearchResults(term)
                      ).then((result) => {
                        if (result.kind === 'result') {
                          callback(
                            result.value?.map((s) => ({
                              value: s.id,
                              label: s.translation,
                            })) ?? []
                          )
                        }
                      }),
                    onChangeValue: (newValue, _) =>
                      setState(
                        componentEditStateUpdaters.componentFieldValueUpdater(
                          'mainIngredients'
                        )((m) =>
                          newValue.map((x) => ({
                            id: x.value,
                            translation: x.label,
                          }))
                        )
                      ),
                  }}
                  worldFlavours={{
                    selected: worldFlavours,
                    options: dataState.value.worldFlavours.map((item) => ({
                      ...item,
                      onChange: () =>
                        setState(
                          componentEditStateUpdaters.componentFieldValueUpdater(
                            'worldFlavours'
                          )(componentEditStateUpdaters.selectedUpdater(item))
                        ),
                    })),
                  }}
                  techniques={{
                    selected: techniques,
                    options: dataState.value.techniques.map((item) => ({
                      ...item,
                      onChange: () =>
                        setState(
                          componentEditStateUpdaters.componentFieldValueUpdater(
                            'techniques'
                          )(componentEditStateUpdaters.selectedUpdater(item))
                        ),
                    })),
                  }}
                  appearances={{
                    selected: appearances,
                    options: dataState.value.appearances.map((item) => ({
                      ...item,
                      onChange: () =>
                        setState(
                          componentEditStateUpdaters.componentFieldValueUpdater(
                            'appearances'
                          )(componentEditStateUpdaters.selectedUpdater(item))
                        ),
                    })),
                  }}
                  textures={{
                    selected: textures,
                    options: dataState.value.textures.map((item) => ({
                      ...item,
                      onChange: () =>
                        setState(
                          componentEditStateUpdaters.componentFieldValueUpdater(
                            'textures'
                          )(componentEditStateUpdaters.selectedUpdater(item))
                        ),
                    })),
                  }}
                />
                <ComponentEditLayout.Footer
                  saveAction={{
                    onClick: () =>
                      setState(
                        componentEditStateUpdaters.startLoadingEditingStateUpdater(
                          componentId
                        )
                      ),
                    asDraft: isDraft(componentFields),
                  }}
                  onCancel={() =>
                    setState(
                      componentEditStateUpdaters.warningUpdater('cancel')
                    )
                  }
                  onDelete={
                    newComponent
                      ? undefined
                      : () =>
                          setState(
                            componentEditStateUpdaters.warningUpdater('delete')
                          )
                  }
                />
              </ComponentEditLayout.Wrapper>
            </>
          ))
        : nothing(),
      warning
        ? fromJSX((setState) =>
            warning === 'delete' ? (
              <ModalLayout
                title={i18next.t(
                  'componentEdit:modalTitleDeleteEditedComponent'
                )}
                onHide={() =>
                  setState(componentEditStateUpdaters.warningUpdater())
                }
                primaryAction={{
                  label: i18next.t(
                    'componentEdit:modalButtonDeleteEditedComponent'
                  ),
                  kind: 'button',
                  onClick: () =>
                    setState(
                      componentEditStateUpdaters.startLoadingDeletingStateUpdater(
                        componentId
                      )
                    ),
                }}
                secondaryAction={{
                  label: i18next.t('common:cancel'),
                  kind: 'button',
                  onClick: () =>
                    setState(componentEditStateUpdaters.warningUpdater()),
                }}
              >
                <ComponentEditLayout.DeleteEditedComponent />
              </ModalLayout>
            ) : (
              <ModalLayout
                title={i18next.t(
                  'componentEdit:modalTitleCancelEditedComponent'
                )}
                onHide={() =>
                  setState(componentEditStateUpdaters.warningUpdater())
                }
                primaryAction={{
                  label: i18next.t(
                    'componentEdit:modalButtonYesCancelEditedComponent'
                  ),
                  kind: 'link',
                  to: componentDetailPath(componentId, originalComponentName),
                }}
                secondaryAction={{
                  label: i18next.t(
                    'componentEdit:modalButtonNoCancelEditedComponent'
                  ),
                  kind: 'button',
                  onClick: () =>
                    setState(componentEditStateUpdaters.warningUpdater()),
                }}
              >
                <ComponentEditLayout.CancelEditedComponent />
              </ModalLayout>
            )
          )
        : nothing(),
    ])
  }
